<script setup>
import { onBeforeUnmount, ref } from 'vue';
import { prefetchPublicProfile } from '@/js/core/utils/profile-api';

// A pointer crossing a list of names should not warm every one it passes over.
const HOVER_INTENT_MS = 20;

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  profileUuid: {
    type: String,
    default: null,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  controls: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['click']);

const buttonRef = ref(null);

const handleClick = () => {
  emit('click', props.profileUuid, buttonRef.value);
};

let prefetchTimeoutId = null;

const startPrefetch = () => {
  if (!props.profileUuid || prefetchTimeoutId !== null) return;

  prefetchTimeoutId = setTimeout(() => {
    prefetchTimeoutId = null;
    prefetchPublicProfile(props.profileUuid);
  }, HOVER_INTENT_MS);
};

const stopPrefetch = () => {
  if (prefetchTimeoutId === null) return;

  clearTimeout(prefetchTimeoutId);
  prefetchTimeoutId = null;
};

// Support mobile Profile prefetch
const prefetchNow = () => {
  stopPrefetch();
  prefetchPublicProfile(props.profileUuid);
};

onBeforeUnmount(stopPrefetch);
</script>

<template>
  <button
    v-if="profileUuid"
    ref="buttonRef"
    type="button"
    class="person-name-link"
    data-person-trigger
    aria-haspopup="dialog"
    :aria-expanded="expanded"
    :aria-controls="expanded && controls ? controls : undefined"
    @click="handleClick"
    @mouseenter="startPrefetch"
    @mouseleave="stopPrefetch"
    @focus="startPrefetch"
    @blur="stopPrefetch"
    @pointerdown="prefetchNow"
  >
    {{ name }}
  </button>
  <span v-else class="person-name-link person-name-link--plain">{{ name }}</span>
</template>
